import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import axios from 'axios';
import { api } from '../../config';
import notify from '../../utils/notify';
import { confirmAction } from '../../utils/confirm';
import logger from '../../utils/logger';
import ActionButton from '../../Components/Common/ActionButton';
import StepActionBar from './component/StepActionBar';
import './component/procurementDetail.css';
import {
  PROCUREMENT_WORK_STEPS,
  PROCUREMENT_TYPE_LABELS,
  isStepCompleted,
} from './utils/procurementHelpers';
import { productTypeNames } from './utils/productTypes';

axios.defaults.withCredentials = true;
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';

const formatMonth = value =>
  value && dayjs(value).isValid() ? dayjs(value).format('MMM YYYY') : null;
const formatDateTime = value =>
  value && dayjs(value).isValid()
    ? `${dayjs(value).format('DD MMM YYYY, HH:mm')} WIB`
    : null;

const ReviewCard = ({ step, index, done, rows, onOpen, isSubmitted }) => (
  <div className='pd-review-card'>
    <div className='pd-review-card__head'>
      <h6>
        {index + 1}. {step.label}
      </h6>
      <span className={`pd-chip ${done ? 'pd-chip--ok' : 'pd-chip--warn'}`}>
        {done ? 'Completed' : 'Not completed'}
      </span>
    </div>
    <dl>
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value ?? '-'}</dd>
        </div>
      ))}
    </dl>
    <button type='button' className='pd-review-card__link' onClick={onOpen}>
      {isSubmitted || done ? 'View' : 'Finish'} {step.label.toLowerCase()}
      <i className='ri-arrow-right-line align-middle ms-1' aria-hidden='true' />
    </button>
  </div>
);

ReviewCard.propTypes = {
  step: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  done: PropTypes.bool,
  rows: PropTypes.array.isRequired,
  onOpen: PropTypes.func.isRequired,
  isSubmitted: PropTypes.bool,
};

const OverviewTab = ({ form, token, setIsLoading, goBack, goToStep }) => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState(null);

  const workSteps = PROCUREMENT_WORK_STEPS;
  const stepDone = workSteps.map(step => isStepCompleted(form, step));
  const allStepsDone = stepDone.every(Boolean);
  const isSubmitted = form.isProcurementCompleted === true;
  const canSubmit = allStepsDone && form.isProcurementCompleted === false;

  const fetchDocuments = useCallback(async () => {
    if (!form.id) return;
    try {
      const response = await fetch(
        `${api.API_URL}/procurement/get-procurement-document?ProcurementId=${form.id}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const result = await response.json();
      setDocuments(result?.data?.items || []);
    } catch (error) {
      logger.warn('Review: could not load documents', error);
    }
  }, [form.id, token]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleSubmit = async () => {
    const confirmed = await confirmAction({
      tone: 'primary',
      title: 'Submit for final validation?',
      message:
        'The procurement is sent for final validation. After this, the procurement becomes read-only.',
      subject: form.procurementNumber,
      confirmLabel: 'Submit',
      cancelLabel: 'Review again',
    });
    if (!confirmed) return;

    try {
      setIsLoading?.(true);
      // The endpoint still records the submission; no signature file is sent.
      const formData = new FormData();
      formData.append('ProcurementId', form.id);

      await axios.post(`${api.API_URL}/digital-signature`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      notify.success(
        'Submitted for final validation. You will be notified when the validation is complete.'
      );
      navigate('/apps-procurement/view');
    } catch (error) {
      logger.error('Review: submission failed', error);
      notify.apiError(error, 'submit the procurement');
    } finally {
      setIsLoading?.(false);
    }
  };

  const contractLabel = form.contractType
    ? parseInt(form.contractType, 10) === 1
      ? 'TERM'
      : 'SPOT'
    : null;
  const contractPeriod =
    contractLabel === 'TERM'
      ? [
          formatMonth(form.contractPeriodTermStart),
          formatMonth(form.contractPeriodTermEnd),
        ]
          .filter(Boolean)
          .join(' – ')
      : formatMonth(form.contractPeriodTermStart);
  const visibleDocuments = documents
    ? documents.filter(doc => doc.isShowToDMUT).length
    : null;
  const confirmationsDone = [
    form.isInformationAboutDMUTConfirmed,
    form.isAcknowledgedDMUT,
  ].filter(Boolean).length;

  const reviewRows = [
    [
      ['Contract', [contractLabel, contractPeriod].filter(Boolean).join(' · ')],
      [
        'Product',
        [form.productName, productTypeNames(form)].filter(Boolean).join(' · '),
      ],
      ['Submission deadline', formatDateTime(form.proposalSubmissionDeadline)],
      ['Offer valid until', formatDateTime(form.proposalValidityPeriodMax)],
    ],
    [
      ['Documents', documents ? documents.length : '…'],
      ['Visible to suppliers', visibleDocuments ?? '…'],
    ],
    [
      ['GTC selected', (form.gtcs || []).length],
      [
        'GTC',
        (form.gtcs || []).map(gtc => gtc.gtcNumber || gtc.title).join(', ') ||
          null,
      ],
    ],
    [
      ['Procurement type', PROCUREMENT_TYPE_LABELS[form.procurementType]],
      ['Suppliers selected', (form.procurementMitra || []).length],
      ['Confirmations', `${confirmationsDone} of 2`],
    ],
  ];

  const firstOpen = workSteps.findIndex((_, i) => !stepDone[i]);

  return (
    <React.Fragment>
      <div className='pd-step-head'>
        <div>
          <h5>
            <i className='ri-file-check-line me-2'></i>
            Review & Submit
          </h5>
          <p>
            Check the summary of each step, then submit this procurement for
            final validation.
          </p>
        </div>
      </div>

      {isSubmitted ? (
        <div className='pd-banner pd-banner--ok'>
          <i className='ri-checkbox-circle-line' aria-hidden='true'></i>
          <div>
            <strong>Submitted for final validation</strong>
            This procurement is read-only.
          </div>
        </div>
      ) : allStepsDone ? (
        <div className='pd-banner pd-banner--ok'>
          <i className='ri-checkbox-circle-line' aria-hidden='true'></i>
          <div>
            <strong>All steps are complete</strong>
            Review the summary below, then submit.
          </div>
        </div>
      ) : (
        <div className='pd-banner pd-banner--warn'>
          <i className='ri-error-warning-line' aria-hidden='true'></i>
          <div>
            <strong>Finish steps 1–{workSteps.length} before submitting</strong>
            {workSteps.filter((_, i) => !stepDone[i]).length} step(s) still
            open.
            <div className='pd-banner__links'>
              {workSteps.map((step, i) =>
                stepDone[i] ? null : (
                  <button
                    key={step.tab}
                    type='button'
                    className='pd-linkchip'
                    onClick={() => goToStep?.(step.tab)}
                  >
                    {step.label}
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      )}

      <div className='pd-review-grid'>
        {workSteps.map((step, i) => (
          <ReviewCard
            key={step.tab}
            step={step}
            index={i}
            done={stepDone[i]}
            rows={reviewRows[i]}
            isSubmitted={isSubmitted}
            onOpen={() => goToStep?.(step.tab)}
          />
        ))}
      </div>

      <StepActionBar
        status={
          isSubmitted
            ? 'Read-only after submission'
            : canSubmit
              ? 'Ready to submit'
              : `Step ${firstOpen + 1} is still open`
        }
        statusTone={isSubmitted ? 'muted' : canSubmit ? 'ok' : 'warn'}
        statusIcon={
          isSubmitted
            ? 'ri-lock-line'
            : canSubmit
              ? 'ri-checkbox-circle-line'
              : 'ri-error-warning-line'
        }
      >
        <ActionButton
          variant='secondary'
          icon='ri-arrow-left-line'
          onClick={() => goBack?.()}
        >
          Supplier Invitation
        </ActionButton>
        {!isSubmitted && (
          <ActionButton
            variant='primary'
            icon='ri-send-plane-line'
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            Submit
          </ActionButton>
        )}
      </StepActionBar>
    </React.Fragment>
  );
};

OverviewTab.propTypes = {
  form: PropTypes.object.isRequired,
  token: PropTypes.string,
  setIsLoading: PropTypes.func,
  goBack: PropTypes.func,
  goToStep: PropTypes.func,
};

export default OverviewTab;
